import { withErrorContext } from ".";
import { isHotkey } from "is-hotkey";
import { last } from "lodash";
import { Editor, Element, Location, Node, NodeEntry, Path, Transforms } from "slate";

import { RichEditorElement, RichTextListElement } from "../types";

import { LIST_FORMATS, isBlockActive } from "./richText";

export function withListBreaks(editor: Editor): Editor {
  const { normalizeNode } = editor;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  editor.normalizeNode = (entry: NodeEntry) => {
    const [node, path] = entry;

    /* Case that we'll have a list with no elements at all is invalid. */
    if (LIST_FORMATS.includes((node as RichTextListElement).type) && Array.from(Node.children(node, [])).length === 0) {
      Transforms.removeNodes(editor, { at: path });
      return;
    }

    /* We want to wrap all direct children of list in a list-item node. */
    if (LIST_FORMATS.includes((node as RichTextListElement).type)) {
      const appliedTransform = Array.from(Node.children(editor, path)).some(([child, childPath]) => {
        if ((child as RichEditorElement).type !== "list-item") {
          Transforms.wrapNodes(editor, { type: "list-item", children: [] }, { at: childPath });
          return true;
        }
        return false;
      });

      if (appliedTransform) {
        return;
      }
    }

    /* We don't want to have stray list items without list parents. */
    if (
      (node as RichEditorElement).type === "list-item" &&
      !LIST_FORMATS.includes((Node.parent(editor, path) as RichTextListElement).type)
    ) {
      Transforms.setNodes(editor, { type: "paragraph" }, { at: path });
      return;
    }

    normalizeNode(entry);
  };

  return editor;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function isEmptyContent(editor: Editor, path: Location) {
  return ["", "\n"].includes(Editor.string(editor, path));
}

function handleReturnRules(event: React.KeyboardEvent<HTMLDivElement>, editor: Editor): boolean {
  const isList = LIST_FORMATS.some(format => isBlockActive(editor, format));

  if (isList) {
    if (!event.shiftKey) {
      const listItemEntry: NodeEntry | undefined = last(
        Array.from(
          Editor.nodes(editor, {
            // FIXME: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            match: (node: any) => Element.isElement(node) && node.type === "list-item",
          })
        )
      );

      if (listItemEntry) {
        const [, listItemPath] = listItemEntry;

        if (isEmptyContent(editor, listItemPath)) {
          event.preventDefault();

          Transforms.splitNodes(editor, { at: listItemPath, always: true });
          const parentPath = Path.parent(listItemPath);
          const nextPath = Path.next(parentPath);
          Transforms.removeNodes(editor, { at: [...nextPath, 0], match: Element.isElement });
          Transforms.insertNodes(
            editor,
            { type: "paragraph", children: [{ text: "" }] },
            { at: nextPath, select: true }
          );

          return true;
        }
      }
    }
  }

  return false;
}

function handleBackspaceRules(event: React.KeyboardEvent<HTMLDivElement>, editor: Editor): boolean {
  const isList = LIST_FORMATS.some(format => isBlockActive(editor, format));

  if (isList) {
    const [listItem] = Editor.nodes(editor, {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      match: (node: any) => Element.isElement(node) && node.type === "list-item",
      mode: "lowest",
    });

    if (listItem) {
      const [, itemPath] = listItem;

      if (isEmptyContent(editor, itemPath)) {
        event.preventDefault();
        Transforms.removeNodes(editor, { at: itemPath, match: Element.isElement, mode: "lowest" });
        return true;
      }
    }
  }

  return false;
}

export const ReactPart: import("./index").ReactPart = {
  onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    editor: Editor,
    next: (event: React.KeyboardEvent<HTMLDivElement>) => void
  ): void {
    if (isHotkey("shift?+return", event.nativeEvent)) {
      const handled = handleReturnRules(event, editor);

      if (!handled) {
        next(event);
      }
    } else if (isHotkey("Backspace", event.nativeEvent)) {
      const handled = handleBackspaceRules(event, editor);

      if (!handled) {
        next(event);
      }
    } else {
      next(event);
    }
  },
};

const pluginSpec: import("./index").PluginSpec = [withErrorContext(withListBreaks), ReactPart];
export default pluginSpec;
